// src/pages/reports/FingerPrintLogs.tsx
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import PageTitle from 'src/components/PageTitle';
import PageTitleWrapper from 'src/components/PageTitleWrapper';
import { Box, Button, Stack, TextField } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import { authenticationService } from 'src/services/authentication.service';
import { get } from 'src/services/httpClient.service';
import { hasPermission } from 'src/components/Permissions/hasPermission';
import reportDownloadService from 'src/services/reportDownload.service';

// Set dayjs locale to use DD/MM/YYYY format
import 'dayjs/locale/en-gb';
dayjs.locale('en-gb');

// Define columns for the report download service
const columns = [
  { field: 'id', headerName: 'ID', width: 90 },
  { field: 'action', headerName: 'Action', flex: 1, minWidth: 80 },
  { field: 'entityName', headerName: 'Entity Name', flex: 1, minWidth: 150 },
  { field: 'description', headerName: 'Description', flex: 1, minWidth: 200 },
  { field: 'command', headerName: 'Command', flex: 1, minWidth: 150 },
  { field: 'status', headerName: 'Status', flex: 1, minWidth: 120 },
  { field: 'createdBy', headerName: 'Created By', flex: 1, minWidth: 150 },
  { 
    field: 'createdDateStr', 
    headerName: 'Created Date', 
    flex: 1, 
    minWidth: 170, // Slightly wider for DD/MM/YYYY format
    valueGetter: (params) => {
      if (!params.row.createdDate) return '';
      return dayjs(params.row.createdDate).format('DD/MM/YYYY HH:mm:ss'); // CHANGED: from YYYY-MM-DD to DD/MM/YYYY
    }
  },
];

const FingerPrintLogs = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState({ pdf: false, excel: false });
  const [pageSize, setPageSize] = useState(100);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs().endOf('month'));
  const { vendorId, franchiseId } = authenticationService.getVendorFranchiseIds();

  useEffect(() => {
    const fetchData = async () => {
      if (!startDate || !endDate) return;

      setLoading(true);
      try {
        const from = dayjs(startDate).format('YYYY-MM-DD');
        const to = dayjs(endDate).format('YYYY-MM-DD');

        const response = await get(
          `${process.env.REACT_APP_BASE_API_URL}/api/reports/vendor/${vendorId}/getAuditReportData?fromDate=${from}&toDate=${to}&entityName=FINGERPRINT`
        );

        const dataWithId = response?.data.map((item: any, index: number) => ({
          id: index + 1,
          createdDateStr: dayjs(item.createdDate).format('DD/MM/YYYY HH:mm:ss'), // CHANGED: from YYYY-MM-DD to DD/MM/YYYY
          ...item,
        }));

        console.log('dataWithId', dataWithId);
        setRows(dataWithId);
       
      } catch (error) {
        console.error('Error fetching audit log data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [startDate, endDate, vendorId]);

  const filteredRows = rows.filter((row) => {
    const matchesSearch = Object.values(row).some(
      (value) =>
        typeof value === 'string' &&
        value.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const matchesEntity = row.entityName === "FINGERPRINT";
    return matchesSearch && matchesEntity;
  });

  // Single download handler for both formats using common service
  const handleDownload = async (format: 'pdf' | 'excel') => {
    if (!startDate || !endDate) {
      alert('Please select both start and end dates');
      return;
    }

    setDownloading(prev => ({ ...prev, [format]: true }));
    
    try {
      const fromStr = dayjs(startDate).format('YYYY-MM-DD');
      const toStr = dayjs(endDate).format('YYYY-MM-DD');
      
      // Generate custom filename
      const customFileName = `FingerPrint_Logs_Report_${fromStr}_to_${toStr}_${dayjs().format('HHmmss')}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      
      // Get unique action types for statistics
      const actionStats = filteredRows.reduce((acc: any, row: any) => {
        const action = row.action || 'Unknown';
        acc[action] = (acc[action] || 0) + 1;
        return acc;
      }, {});

      const success = await reportDownloadService.downloadReport({
        title: 'Fingerprint Logs Report',
        columns,
        rows: filteredRows,
        month: startDate.month() + 1,
        year: startDate.year(),
        fileName: customFileName,
        
        // Excel specific configuration
        excelHeaders: [
          "ID",
          "Action",
          "Entity Name",
          "Description",
          "Command",
          "Status",
          "Created By",
          "Created Date"
        ],
        excelRowMapper: (row: any) => [
          row.id || '',
          row.action || '',
          row.entityName || '',
          row.description || '',
          row.command || '',
          row.status || '',
          row.createdBy || '',
          row.createdDateStr || dayjs(row.createdDate).format('DD/MM/YYYY HH:mm:ss') || '' // CHANGED: from YYYY-MM-DD to DD/MM/YYYY
        ]
      }, format);
      
      if (!success) {
        alert(`Failed to generate ${format.toUpperCase()}. Please try again.`);
      }
    } catch (error) {
      console.error(`Error downloading ${format}:`, error);
      alert(`Error generating ${format.toUpperCase()}. Please try again.`);
    } finally {
      setDownloading(prev => ({ ...prev, [format]: false }));
    }
  };

  return (
    <>
      <Helmet>
        <title>Fingerprint Logs</title>
      </Helmet>

      <PageTitleWrapper>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <Box>
            <PageTitle
              heading="Fingerprint Logs"
              subHeading="Track fingerprinting operations"
              handleClick={undefined}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {hasPermission(["OWNER"]) && (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleDownload('excel')}
                  disabled={downloading.excel}
                >
                  {downloading.excel ? 'Generating...' : 'Download Excel'}
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<PictureAsPdfIcon />}
                  onClick={() => handleDownload('pdf')}
                  disabled={downloading.pdf}
                >
                  {downloading.pdf ? 'Generating...' : 'Download PDF'}
                </Button>
              </>
            )}
          </Box>
        </Box>
      </PageTitleWrapper>

      {/* Filter Section */}
      <Box sx={{ px: 2 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }} alignItems="center">
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ minWidth: 200 }}
            />
          
            <DatePicker
              label="Start Date (DD/MM/YYYY)" // CHANGED: added format hint
              value={startDate}
              onChange={(newValue) => setStartDate(newValue)}
              format="DD/MM/YYYY" // ADDED: explicit format
              slotProps={{ 
                textField: { 
                  size: 'small', 
                  sx: { minWidth: 180 },
                  placeholder: 'DD/MM/YYYY' // ADDED: placeholder
                } 
              }}
            />
            <DatePicker
              label="End Date (DD/MM/YYYY)" // CHANGED: added format hint
              value={endDate}
              onChange={(newValue) => setEndDate(newValue)}
              format="DD/MM/YYYY" // ADDED: explicit format
              slotProps={{ 
                textField: { 
                  size: 'small', 
                  sx: { minWidth: 180 },
                  placeholder: 'DD/MM/YYYY' // ADDED: placeholder
                } 
              }}
            />
          </Stack>
        </LocalizationProvider>
      </Box>

      {/* Table Section */}
      <Box sx={{ height: 'calc(100vh - 220px)', width: '100%', padding: '15px' }}>
        <DataGrid
          rows={filteredRows}
          columns={columns}
          loading={loading}
          pagination
          pageSize={pageSize}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          rowsPerPageOptions={[5, 10, 20, 50, 100]}
          getRowHeight={() => 'auto'}
          sx={{
            boxShadow: 'none',
            border: 2,
            borderColor: 'rgb(43 44 45)',
            '& .MuiDataGrid-cell': {
              alignItems: 'start',
              py: 1.5
            },
          }}
        />
      </Box>
    </>
  );
};

export default FingerPrintLogs;